"use client";

import type { ReactNode } from "react";

// Building blocks for the Statistics dashboard. Every card and tile is marked
// so you can tell at a glance what's real: a green "Live" card shows measured
// data, an amber "Partly live" card mixes real and placeholder tiles, and an
// orange "Coming soon" card (or a dimmed tile with a "Soon" tag) is a
// placeholder for data the platform doesn't collect yet.

export type Availability = "live" | "partial" | "soon";

const BADGE: Record<Availability, { label: string; cls: string; dot: string }> = {
  live: { label: "Live", cls: "bg-green-100 text-accent-green", dot: "bg-accent-green" },
  partial: { label: "Partly live", cls: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  soon: { label: "Coming soon", cls: "bg-orange-100 text-accent-orange", dot: "bg-accent-orange" },
};

export function AvailabilityBadge({ kind }: { kind: Availability }) {
  const b = BADGE[kind];
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${b.cls}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${b.dot}`} />
      {b.label}
    </span>
  );
}

export function SoonTag() {
  return (
    <span className="rounded bg-orange-100 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-accent-orange">
      Soon
    </span>
  );
}

export function StatsCard({
  icon,
  iconBg,
  title,
  subtitle,
  availability,
  showBadge = true,
  className = "",
  children,
}: {
  icon: ReactNode;
  iconBg: string;
  title: string;
  subtitle: string;
  availability: Availability;
  showBadge?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section
      className={`flex flex-col rounded-xl border bg-bg-card p-4 shadow-sm ${
        availability === "soon" ? "border-dashed border-orange-300" : "border-border-light"
      } ${className}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white"
          style={{ backgroundColor: iconBg }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-5 w-5"
            aria-hidden="true"
          >
            {icon}
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h2 className="text-sm font-bold leading-tight">{title}</h2>
            {showBadge && <AvailabilityBadge kind={availability} />}
          </div>
          <p className="mt-0.5 text-xs text-text-secondary">{subtitle}</p>
        </div>
      </div>
      <div className="mt-4 flex min-h-0 flex-1 flex-col gap-3">{children}</div>
    </section>
  );
}

export function Tile({
  label,
  value,
  sub,
  trend,
  soon = false,
  className = "",
}: {
  label: string;
  value?: string;
  sub?: string;
  trend?: number | null; // percent change; omitted when unknown
  soon?: boolean;
  className?: string;
}) {
  return (
    <div
      className={`rounded-lg border p-2.5 ${
        soon ? "border-dashed border-border-light bg-bg-main/50" : "border-border-light"
      } ${className}`}
    >
      <div className="flex items-center justify-between gap-1 text-[11px] text-text-secondary">
        <span className="truncate">{label}</span>
        {soon && <SoonTag />}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className={`text-xl font-bold ${soon ? "text-text-tertiary" : ""}`}>{soon ? "—" : value}</span>
        {!soon && trend !== undefined && trend !== null && (
          <span className={`text-xs font-semibold ${trend >= 0 ? "text-accent-green" : "text-accent-red"}`}>
            {trend >= 0 ? "↑" : "↓"} {Math.abs(trend)}%
          </span>
        )}
      </div>
      {sub && !soon && <div className="text-[11px] text-text-tertiary">{sub}</div>}
    </div>
  );
}

// A grey ring standing in for a donut chart that has no data yet.
export function PlaceholderDonut({ label }: { label?: string }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative h-20 w-20">
        <svg viewBox="0 0 36 36" className="h-full w-full -rotate-90" aria-hidden="true">
          <circle cx="18" cy="18" r="14" fill="none" stroke="#e2e8f0" strokeWidth="4" strokeDasharray="3 2" />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-text-tertiary">
          —
        </span>
      </div>
      {label && <span className="text-[11px] font-semibold text-text-secondary">{label}</span>}
    </div>
  );
}

// A real donut from a few slices, with the total (or a label) in the middle.
export function Donut({
  slices,
  center,
  caption,
}: {
  slices: { label: string; value: number; color: string }[];
  center?: string;
  caption?: string;
}) {
  const total = slices.reduce((a, s) => a + s.value, 0);
  const radius = 14;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative h-20 w-20">
        <svg viewBox="0 0 36 36" className="h-full w-full -rotate-90" role="img" aria-label={caption ?? "Chart"}>
          <circle cx="18" cy="18" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="4" />
          {total > 0 &&
            slices.map((s) => {
              const length = (s.value / total) * circumference;
              const circle = (
                <circle
                  key={s.label}
                  cx="18"
                  cy="18"
                  r={radius}
                  fill="none"
                  stroke={s.color}
                  strokeWidth="4"
                  strokeDasharray={`${length} ${circumference - length}`}
                  strokeDashoffset={-offset}
                />
              );
              offset += length;
              return circle;
            })}
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-sm font-bold">{center ?? ""}</span>
      </div>
      {caption && <span className="text-[11px] font-semibold text-text-secondary">{caption}</span>}
    </div>
  );
}

export const SLICE_COLORS = ["#2563eb", "#9333ea", "#0d9488", "#ea580c", "#ca8a04", "#64748b", "#dc2626"];

// A greyed list of rows for tables/leaderboards that have no data yet.
export function PlaceholderRows({ rows, columns }: { rows: number; columns: string[] }) {
  return (
    <div className="rounded-lg border border-dashed border-border-light bg-bg-main/50 p-2.5 text-[11px]">
      <div className="mb-1.5 flex justify-between font-semibold text-text-secondary">
        {columns.map((c) => (
          <span key={c}>{c}</span>
        ))}
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center justify-between border-t border-border-light py-1.5">
          <span className="h-2 w-24 rounded bg-gray-200" />
          <span className="h-2 w-8 rounded bg-gray-200" />
        </div>
      ))}
    </div>
  );
}

export function SoonNote({ children }: { children: ReactNode }) {
  return (
    <p className="mt-auto rounded-lg bg-orange-50 px-3 py-2 text-[11px] text-text-secondary">{children}</p>
  );
}

export function UsageBar({
  label,
  value,
  percent,
  color,
  title,
}: {
  label: string;
  value: string;
  percent: number;
  color: string;
  title?: string;
}) {
  return (
    <div title={title} className="grid grid-cols-[96px_1fr_auto] items-center gap-2 text-[11px]">
      <span className="text-text-secondary">{label}</span>
      <div className="h-1.5 overflow-hidden rounded-full bg-border-light">
        <div
          className="h-full rounded-full"
          style={{ width: `${Math.max(2, Math.min(100, percent))}%`, backgroundColor: color }}
        />
      </div>
      <span className="font-semibold">{value}</span>
    </div>
  );
}
